import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ToastService } from 'src/app/core/services/toast.service';
import { Fornecedor } from '../../core/models/fornecedor';
import { FornecedorService } from 'src/app/core/services/fornecedor.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-fornecedor',
  templateUrl: './fornecedor.component.html',
  styleUrls: ['./fornecedor.component.scss'],
})
export class FornecedorComponent implements OnInit, OnDestroy {
  fornecedor = new Fornecedor();
  fornecedores: Fornecedor[] = [];

  // edição
  editMode = false;
  editFornecedorId: number | null = null;

  @ViewChild('fornecedorForm') fornecedorForm?: NgForm;

  // loading
  isSubmitting = false;

  private destroy$ = new Subject<void>();

  constructor(
    private readonly servico: FornecedorService,
    private readonly messageService: MessageService,
    private readonly confirmation: ConfirmationService,
    private readonly toast: ToastService
  ) {
    // carregar lista ao construir
    this.selecionar();
  }

  get canSubmit(): boolean {
    return !!(this.fornecedor && (this.fornecedor.nome || '').toString().trim() && (this.fornecedor.celular || '').toString().trim());
  }

  selecionar(): void {
    this.servico
      .selecionar()
      .subscribe((retorno) => (this.fornecedores = retorno));
  }

  cadastrar(): void {
    this.isSubmitting = true;
    this.servico.cadastrar(this.fornecedor).subscribe({
      next: (retorno) => {
        this.fornecedores.push(retorno);
        this.fornecedor = new Fornecedor();
        this.toast.success('Fornecedor cadastrado com sucesso.');
      },
      error: (err) => this.handleError(err, 'Erro ao cadastrar fornecedor.'),
      complete: () => (this.isSubmitting = false),
    });
  }

  editar(c: Fornecedor): void {
    this.editMode = true;
    this.editFornecedorId = c.id ?? null;
    this.fornecedor = { ...c } as Fornecedor;
  }

  salvar(): void {
    if (!this.canSubmit) {
      this.toast.warn('Preencha nome e celular antes de salvar.');
      return;
    }

    this.isSubmitting = true;

    if (this.editMode && this.editFornecedorId != null) {
      // atualizar
      this.servico.alterar(this.editFornecedorId, this.fornecedor).subscribe({
        next: (ret) => {
          const idx = this.fornecedores.findIndex((f) => f.id === ret.id);
          if (idx >= 0) this.fornecedores[idx] = ret;
          else this.fornecedores.unshift(ret);

          this.toast.success('Fornecedor atualizado com sucesso.');
          this.limparFormulario();
        },
        error: (err) => this.handleError(err, 'Erro ao atualizar fornecedor.'),
        complete: () => (this.isSubmitting = false),
      });
    } else {
      // criar
      this.servico.cadastrar(this.fornecedor).subscribe({
        next: (retorno) => {
          this.fornecedores.push(retorno);
          this.fornecedor = new Fornecedor();
          this.toast.success('Fornecedor cadastrado com sucesso.');
        },
        error: (err) => this.handleError(err, 'Erro ao cadastrar fornecedor.'),
        complete: () => (this.isSubmitting = false),
      });
    }
  }

  limparFormulario(): void {
    this.fornecedor = new Fornecedor();
    this.editMode = false;
    this.editFornecedorId = null;
    // reset template form if available
    try {
      this.fornecedorForm?.resetForm();
    } catch {}
  }

  cancelarEdicao(): void {
    this.editMode = false;
    this.editFornecedorId = null;
    this.fornecedor = new Fornecedor();
  }

    salvarAlteracao(): void {
    if (!this.editMode || this.editFornecedorId == null) return;
    this.servico.alterar(this.editFornecedorId, this.fornecedor).subscribe({
      next: (ret) => {
        const idx = this.fornecedores.findIndex((x) => x.id === this.editFornecedorId);
        if (idx >= 0) this.fornecedores[idx] = ret;
        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Fornecedor alterado com sucesso.' });
        this.cancelarEdicao();
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao alterar fornecedor.' });
        console.error('Erro ao alterar fornecedor:', err);
      },
    });
  }

  confirmarAtivarDesativar(fornecedor: Fornecedor | undefined): void {
    if (!fornecedor?.id) return;
    const willDeactivate = !!fornecedor.ativo;
    const actionLabel = willDeactivate ? 'desativar' : 'ativar';

    this.confirmation.confirm({
      message: `Confirma ${actionLabel} o fornecedor ${fornecedor.nome || fornecedor.id}?`,
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        this.servico.ativarDesativar(fornecedor.id as number, !willDeactivate).subscribe({
          next: (ret) => {
            const idx = this.fornecedores.findIndex((u) => u.id === fornecedor.id);
            if (idx >= 0) {
              if (ret) this.fornecedores[idx] = ret;
              else this.fornecedores[idx].ativo = !willDeactivate;
            }
            this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: `Fornecedor ${willDeactivate ? 'desativado' : 'ativado'} com sucesso.` });
          },
          error: (err) => {
            const msg = err?.error?.message || err?.message || `Erro ao ${actionLabel} fornecedor`;
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: msg });
            console.error(`Erro ao ${actionLabel} fornecedor:`, err);
          },
        });
      },
    });
  }

  private handleError(err: any, defaultMsg: string) {
    const msg = err?.error?.message || err?.message || defaultMsg;
    this.toast.error(msg);
    console.error(defaultMsg, err);
  }

  ngOnInit(): void {
    this.selecionar();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
